import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { format } from 'date-fns';

// Shown under any rejected step (OmniDrive or Account rejection) - the assignment goes back to the vendor to revise.
// Wording under a rejected step. The vendor themself is told what to do next; everyone else sees who it went to.
const revisionSublabel = (vendor, forVendor = false) =>
  forVendor
    ? 'Reassigned to you. Make necessary changes and resubmit'
    : `Reassigned to vendor${vendor ? ` (${vendor})` : ''}. Revision requested`;

/**
 * StatusTimeline — horizontal lifecycle progress bar.
 *
 * Props:
 *   fgCreatedAt        — when the FG was created
 *   assignedAt         — when vendor was assigned
 *   submittedAt        — when vendor submitted BOM
 *   reviewedAt         — when OmniDrive reviewed (approved or rejected)
 *   fgClientApprovedAt — when client approved/rejected
 *   status             — current assignment status string
 *   companyNotes       — OmniDrive notes (shown on rejection)
 *   vendorName         — vendor contact's name (fallback for the label below)
 *   vendorCompanyName  — vendor company name, shown under Assigned / Submitted steps (preferred)
 *   assignmentId       — used to load audit history
 *   fetchHistory       — async fn(id) => history array
 *   companyLabel       — label for OmniDrive step  (default "OmniDrive")
 *   clientLabel        — label for client step
 *
 * Full-lifecycle multi-cycle mode:
 *   assignments        — AssignmentSummary[] (all cycles)
 *   fgStatus           — overall FG status
 */
export default function StatusTimeline({
  fgCreatedAt,
  assignedAt,
  submittedAt,
  reviewedAt,
  fgClientApprovedAt,
  status,
  companyNotes,
  vendorName,
  vendorCompanyName,
  assignmentId,
  fetchHistory,
  companyLabel = 'OmniDrive',
  clientLabel  = 'Account',
  // full-lifecycle mode
  assignments,
  fgStatus,
  // email → display-name map so history never leaks PII to clients
  performerAliases = {},
  // set true for OmniDrive views — shows raw email/username instead of masked role label
  showRawPerformer = false,
  // set true to hide the "View History" toggle (e.g. when AuditLogPanel is already on the page)
  hideHistory = false,
  // set true when the logged-in viewer is the vendor (changes the wording under rejected steps)
  viewerIsVendor = false,
}) {
  const [showHistory, setShowHistory] = useState(false);

  // ── When all assignments are passed, derive a unified set of props ─────────
  // Sort oldest-first; build a merged view across all cycles.
  let resolvedSteps = null;
  if (assignments && assignments.length > 0) {
    const sorted = [...assignments].sort(
      (a, b) => new Date(a.assignedAt || 0) - new Date(b.assignedAt || 0)
    );
    resolvedSteps = buildMultiCycleSteps(sorted, fgCreatedAt, fgClientApprovedAt, fgStatus, companyLabel, clientLabel, viewerIsVendor);
  }

  // ── Single-assignment steps ────────────────────────────────────────────────
  const isCancelled       = status === 'CANCELLED';
  const isCompanyRejected = status === 'COMPANY_REJECTED';
  // For CANCELLED: company step shows as done if reviewedAt is set (cancelled after OmniDrive reviewed)
  const isCompanyDone     = ['COMPANY_APPROVED','PENDING_FINAL_APPROVAL','CLIENT_APPROVED','CLIENT_REJECTED','CVA_REJECTED'].includes(status)
    || (isCancelled && !!reviewedAt);
  const isAccountDone     = ['PENDING_FINAL_APPROVAL','CLIENT_APPROVED','CLIENT_REJECTED','CVA_REJECTED'].includes(status);
  const isCvaRejected     = status === 'CVA_REJECTED';
  const isAccountPending  = status === 'COMPANY_APPROVED';
  const isCvaDone         = ['CLIENT_APPROVED'].includes(status);
  const isCvaPending      = status === 'PENDING_FINAL_APPROVAL';
  const isClientDone      = ['CLIENT_APPROVED','CLIENT_REJECTED'].includes(status);
  const isClientRejected  = status === 'CLIENT_REJECTED';
  const isCompleted       = status === 'CLIENT_APPROVED';
  const isRejected        = ['COMPANY_REJECTED','CLIENT_REJECTED','CVA_REJECTED'].includes(status);

  const singleSteps = [
    {
      key: 'created', label: 'Created',
      date: fgCreatedAt, done: !!fgCreatedAt, active: !assignedAt,
    },
    {
      key: 'assigned', label: 'Vendor Assigned',
      date: assignedAt, done: !!assignedAt,
      active: !!assignedAt && !submittedAt && !isCompanyRejected,
      extra: vendorCompanyName || vendorName || null,
    },
    {
      key: 'submitted', label: 'Vendor Responded',
      date: submittedAt, done: !!submittedAt,
      active: false,
      extra: vendorCompanyName || vendorName || null,
    },
    {
      key: 'awaiting-review',
      label: `Awaiting ${companyLabel} Review`,
      sublabel: (isCompanyDone || isCompanyRejected) ? null : 'Awaiting',
      date: null,
      done: isCompanyDone || isCompanyRejected,
      active: !!submittedAt && !reviewedAt && !isCompanyRejected && !isCancelled,
    },
    {
      key: 'company',
      label:    isCompanyRejected ? `${companyLabel} Rejected` : `${companyLabel} Approved`,
      sublabel: isCompanyRejected ? revisionSublabel(vendorCompanyName || vendorName, viewerIsVendor) : (isCompanyDone ? null : 'Awaiting'),
      date: reviewedAt, done: isCompanyDone,
      active: false,
      rejected: isCompanyRejected,
    },
    {
      key: 'client',
      label:    isClientRejected ? `${clientLabel} Rejected` : `${clientLabel} Review`,
      sublabel: isClientRejected ? revisionSublabel(vendorCompanyName || vendorName, viewerIsVendor) : (isAccountDone ? 'Approved' : 'Awaiting'),
      date: null,
      done: isAccountDone, active: isAccountPending,
      rejected: isClientRejected, skip: isCompanyRejected || isCancelled,
    },
    {
      key: 'cva',
      label:    isCvaRejected ? 'CVA Rejected' : 'CVA Final Approval',
      sublabel: isCvaRejected ? revisionSublabel(vendorCompanyName || vendorName, viewerIsVendor) : (isCvaDone ? 'Approved & Synced' : (isCvaPending ? 'Awaiting CVA' : 'Awaiting')),
      date: isCompleted ? fgClientApprovedAt : null,
      done: isCvaDone || isCvaRejected, active: isCvaPending,
      rejected: isCvaRejected, skip: isCompanyRejected || isCancelled,
    },
    {
      key: 'completed', label: 'Completed', sublabel: 'Fully approved',
      date: isCompleted ? fgClientApprovedAt : null,
      done: isCompleted, active: false,
      skip: isCompanyRejected || isClientRejected || isCvaRejected || isCancelled,
    },
    {
      key: 'cancelled', label: 'Cancelled', sublabel: 'Permanently cancelled',
      date: null, done: true, active: false,
      rejected: true, skip: !isCancelled,
    },
  ].filter(s => !s.skip);

  const steps = resolvedSteps ?? singleSteps;

  // For history: use the last assignment's id when in multi-cycle mode
  const historyId = assignmentId ?? (assignments ? assignments[assignments.length - 1]?.assignmentId : null);
  const hasHistory = !!historyId && !!fetchHistory;

  const { data: historyData, isLoading: historyLoading } = useQuery(
    ['assignment-history', historyId],
    () => fetchHistory(historyId),
    { enabled: showHistory && hasHistory, refetchOnWindowFocus: false }
  );
  const history = historyData?.data || [];

  // Rejection notes to show (single-cycle)
  const showRejectionNotes = isCompanyRejected && companyNotes && !assignments;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-sm font-semibold text-gray-700">Request Lifecycle</h3>
        {hasHistory && !hideHistory && (
          <button onClick={() => setShowHistory(v => !v)}
            className="text-xs font-medium text-gray-500 hover:text-gray-700 flex items-center gap-1 px-2.5 py-1 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-3.5 w-3.5">
              <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" />
            </svg>
            {showHistory ? 'Hide History' : 'View History'}
          </button>
        )}
      </div>

      {/* ── Horizontal step row ───────────────────────────────────────────── */}
      <div className="relative flex items-start">
        {steps.map((step, idx) => {
          const isLast = idx === steps.length - 1;
          return (
            <div key={step.key} className="flex-1 flex flex-col items-center relative min-w-0">
              {/* Connector line */}
              {!isLast && (
                <div className="absolute top-3.5 left-1/2 w-full h-0.5 z-0"
                  style={{ background: step.done ? (step.rejected ? '#ef4444' : '#22c55e') : '#e5e7eb' }} />
              )}

              {/* Dot */}
              <div className="relative z-10 mb-2 flex-shrink-0">
                {step.rejected ? (
                  <div className="h-7 w-7 rounded-full bg-red-500 border-2 border-red-500 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2.5} className="h-3.5 w-3.5">
                      <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" />
                    </svg>
                  </div>
                ) : step.done ? (
                  <div className="h-7 w-7 rounded-full bg-green-500 border-2 border-green-500 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2.5} className="h-3.5 w-3.5">
                      <path d="M5 13l4 4L19 7" strokeLinecap="round" />
                    </svg>
                  </div>
                ) : step.active ? (
                  <div className="h-7 w-7 rounded-full border-2 border-blue-500 bg-blue-50 flex items-center justify-center">
                    <div className="h-2.5 w-2.5 rounded-full bg-blue-500 animate-pulse" />
                  </div>
                ) : (
                  <div className="h-7 w-7 rounded-full border-2 border-gray-200 bg-gray-50 flex items-center justify-center">
                    <div className="h-2 w-2 rounded-full bg-gray-300" />
                  </div>
                )}
              </div>

              {/* Label */}
              <p className={`text-xs font-semibold text-center leading-tight px-1 ${
                step.rejected ? 'text-red-600' :
                step.done     ? 'text-gray-800' :
                step.active   ? 'text-blue-600' : 'text-gray-400'
              }`}>{step.label}</p>

              {/* Date or sublabel */}
              {step.date ? (
                <p className="text-xs text-gray-400 text-center mt-0.5 leading-tight">
                  {format(new Date(step.date), 'dd MMM yyyy')}
                  <br /><span className="text-gray-400">{format(new Date(step.date), 'HH:mm')}</span>
                  {step.rejected && step.sublabel && (
                    <><br /><span className="text-red-400">{step.sublabel}</span></>
                  )}
                </p>
              ) : step.sublabel ? (
                <p className={`text-xs text-center mt-0.5 leading-tight ${step.rejected ? 'text-red-400' : 'text-gray-400'}`}>
                  {step.sublabel}
                </p>
              ) : null}

              {/* Vendor name */}
              {step.extra && (
                <p className="text-xs text-blue-500 font-medium text-center mt-0.5 leading-tight px-1 truncate max-w-full">
                  {step.extra}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Rejection notes ───────────────────────────────────────────────── */}
      {showRejectionNotes && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-xs font-semibold text-red-700 mb-0.5">{companyLabel} Rejection Notes</p>
          <p className="text-sm text-red-800">{companyNotes}</p>
        </div>
      )}

      {/* ── History panel ─────────────────────────────────────────────────── */}
      {showHistory && (
        <div className="mt-4 border-t border-gray-100 pt-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Full Activity History</p>
          {historyLoading ? (
            <p className="text-xs text-gray-400 text-center py-3">Loading history…</p>
          ) : history.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-3">No history recorded.</p>
          ) : (
            <div className="space-y-2.5">
              {history.map((entry) => (
                <HistoryEntry
                  key={entry.id}
                  entry={entry}
                  showRawPerformer={showRawPerformer}
                  performerAliases={performerAliases}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── History entry with BOM-delete expansion ───────────────────────────────────

const BOM_DELETE_ACTIONS = new Set([
  'BOM_COMPONENT_DELETED_BY_ADMIN',
  'BOM_COMPONENT_DELETED_BY_CLIENT',
  'BOM_COMPONENT_DELETED_BY_VENDOR',
]);

const BOM_EDIT_ACTIONS = new Set([
  'BOM_UPDATED_BY_ADMIN',
  'BOM_UPDATED_BY_CLIENT',
]);

function HistoryEntry({ entry, showRawPerformer, performerAliases }) {
  const [expand, setExpand] = useState(false);
  const isBomDelete = BOM_DELETE_ACTIONS.has(entry.action);
  const isBomEdit   = BOM_EDIT_ACTIONS.has(entry.action);
  const needsExpand = isBomDelete || isBomEdit;

  let diffRows = null;
  if (needsExpand && entry.details) {
    try {
      const parsed = JSON.parse(entry.details);
      if (Array.isArray(parsed)) diffRows = parsed;
    } catch (_) {}
  }

  const deletedName = isBomDelete && diffRows?.length
    ? (diffRows[0].field || '').split(' · ')[0] || 'BOM Component'
    : null;

  const actionColor =
    entry.action?.includes('REJECTED') || entry.action === 'CANCELLED' || isBomDelete
      ? 'text-red-600'
      : entry.action?.includes('APPROVED') ? 'text-green-600'
      : entry.action?.includes('SUBMITTED') ? 'text-blue-600'
      : isBomEdit ? 'text-amber-600'
      : 'text-gray-700';

  return (
    <div className="flex gap-3 items-start">
      <div className="mt-1 h-2 w-2 rounded-full bg-gray-300 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-xs font-semibold ${actionColor}`}>{formatAction(entry.action)}</span>
          <span className="text-xs text-gray-400">
            by {showRawPerformer
              ? (entry.performedBy || '—')
              : roleLabel(entry.action, entry.performedBy, performerAliases)}
          </span>
          <span className="text-xs text-gray-500">
            {entry.performedAt ? format(new Date(entry.performedAt), 'dd MMM yyyy HH:mm') : ''}
          </span>
        </div>

        {/* BOM delete — collapsible field snapshot */}
        {isBomDelete && diffRows && (
          <div className="mt-1">
            <button
              onClick={() => setExpand(v => !v)}
              className="text-xs text-red-600 hover:text-red-800 underline underline-offset-2 font-medium">
              {expand ? 'Hide deleted fields' : `"${deletedName}" — show deleted fields`}
            </button>
            {expand && (() => {
              const visibleRows = diffRows.filter(row => row.oldValue && row.oldValue !== '—');
              return (
                <div className="mt-1.5 rounded-lg border border-red-100 overflow-x-auto">
                  <table className="text-xs whitespace-nowrap">
                    <thead>
                      <tr className="bg-red-50 border-b border-red-100">
                        {visibleRows.map((row, i) => {
                          const fieldLabel = (row.field || '').includes(' · ')
                            ? row.field.split(' · ').slice(1).join(' · ')
                            : row.field;
                          return (
                            <th key={i} className="px-3 py-1.5 text-left font-semibold text-gray-500 uppercase tracking-wide border-r border-red-100 last:border-r-0">
                              {fieldLabel}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {visibleRows.map((row, i) => (
                          <td key={i} className="px-3 py-2 border-r border-red-50 last:border-r-0">
                            <span className="inline-block px-1.5 py-0.5 bg-red-50 border border-red-100 rounded text-red-700 font-mono">
                              {row.oldValue}
                            </span>
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              );
            })()}
            )}
          </div>
        )}

        {/* BOM edit — collapsible diff */}
        {isBomEdit && diffRows && (
          <div className="mt-1">
            <button
              onClick={() => setExpand(v => !v)}
              className="text-xs text-blue-600 hover:text-blue-800 underline underline-offset-2 font-medium">
              {expand ? 'Hide changes' : `${diffRows.length} field${diffRows.length !== 1 ? 's' : ''} changed — show`}
            </button>
            {expand && (
              <div className="mt-1.5 rounded-lg border border-gray-100 overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="px-3 py-1.5 text-left font-semibold text-gray-500 uppercase tracking-wide">Field</th>
                      <th className="px-3 py-1.5 text-left font-semibold text-gray-500 uppercase tracking-wide">Before</th>
                      <th className="px-3 py-1.5 text-left font-semibold text-gray-500 uppercase tracking-wide">After</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {diffRows.map((row, i) => (
                      <tr key={i}>
                        <td className="px-3 py-1.5 text-gray-600 font-medium whitespace-nowrap">{row.field}</td>
                        <td className="px-3 py-1.5 max-w-[160px]">
                          <span className="inline-block px-1.5 py-0.5 bg-red-50 border border-red-100 rounded text-red-700 font-mono break-words">{row.oldValue}</span>
                        </td>
                        <td className="px-3 py-1.5 max-w-[160px]">
                          <span className="inline-block px-1.5 py-0.5 bg-green-50 border border-green-100 rounded text-green-700 font-mono break-words">{row.newValue}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Fallback plain text for non-BOM or unparseable details */}
        {!needsExpand && entry.details && (
          <p className="text-xs text-gray-500 mt-0.5 break-words">{entry.details}</p>
        )}
        {needsExpand && !diffRows && entry.details && (
          <p className="text-xs text-gray-500 mt-0.5 break-words">{entry.details}</p>
        )}
      </div>
    </div>
  );
}

// ── Multi-cycle step builder ──────────────────────────────────────────────────
// Flattens all assignment cycles into a single horizontal step sequence.
// Pattern: Created → [Assigned → Submitted → Review]×N → Client Review → Completed

function buildMultiCycleSteps(sorted, fgCreatedAt, fgClientApprovedAt, fgStatus, companyLabel, clientLabel, viewerIsVendor = false) {
  const steps = [];
  const multi = sorted.length > 1;

  const isCancelled  = fgStatus === 'CANCELLED';
  const isClientDone = ['CLIENT_APPROVED','CLIENT_REJECTED'].includes(fgStatus);
  const isCompleted  = fgStatus === 'CLIENT_APPROVED';
  const isClientRej  = fgStatus === 'CLIENT_REJECTED';
  const latestAsgn   = sorted[sorted.length - 1];
  const latestVendor = latestAsgn ? (latestAsgn.vendorCompanyName || latestAsgn.vendorName) : null;
  const isCvaRej     = fgStatus === 'CVA_REJECTED';

  // Created
  steps.push({
    key: 'created', label: 'Created',
    date: fgCreatedAt, done: !!fgCreatedAt, active: false,
  });

  sorted.forEach((asgn, i) => {
    const suffix          = multi ? ` (${i + 1})` : '';
    const isRej           = asgn.status === 'COMPANY_REJECTED';
    const isCancelledAsgn = asgn.status === 'CANCELLED';
    const isApproved      = ['COMPANY_APPROVED','PENDING_FINAL_APPROVAL','CLIENT_APPROVED','CLIENT_REJECTED','CVA_REJECTED'].includes(asgn.status);
    // For a cancelled assignment, show OmniDrive review as done if reviewedAt is set
    // Derive from status, not reviewedAt: a resubmitted assignment may still carry an older review timestamp.
    const reviewDone      = isApproved || isRej || (isCancelledAsgn && !!asgn.reviewedAt);
    const reviewPend      = !!asgn.submittedAt && !isApproved && !isRej && !isCancelledAsgn;

    steps.push({
      key:    `assigned-${asgn.assignmentId}`,
      label:  `Vendor Assigned${suffix}`,
      date:   asgn.assignedAt, done: !!asgn.assignedAt,
      active: !!asgn.assignedAt && !asgn.submittedAt && !isCancelledAsgn,
      extra:  asgn.vendorCompanyName || asgn.vendorName || null,
    });

    steps.push({
      key:     `submitted-${asgn.assignmentId}`,
      label:   `Vendor Responded${suffix}`,
      sublabel: !asgn.submittedAt ? 'Pending' : null,
      date:    asgn.submittedAt, done: !!asgn.submittedAt,
      active:  false,
      extra:   asgn.vendorCompanyName || asgn.vendorName || null,
    });

    steps.push({
      key:    `awaiting-review-${asgn.assignmentId}`,
      label:  `Awaiting ${companyLabel} Review${suffix}`,
      sublabel: isCancelledAsgn && !asgn.reviewedAt
        ? 'Cancelled before review'
        : (reviewDone || isApproved || isRej) ? null : 'Awaiting',
      date:   null,
      done:   reviewDone || isApproved || isRej,
      active: reviewPend,
    });

    steps.push({
      key:      `review-${asgn.assignmentId}`,
      label:    isRej           ? `${companyLabel} Rejected${suffix}`
              : isCancelledAsgn ? `${companyLabel} Review${suffix}`
              :                   `${companyLabel} Approved${suffix}`,
      sublabel: isRej ? revisionSublabel(asgn.vendorCompanyName || asgn.vendorName, viewerIsVendor) : (!asgn.reviewedAt ? (isCancelledAsgn ? 'Cancelled before review' : null) : null),
      date:     reviewDone ? asgn.reviewedAt : null,
      done:     reviewDone || isApproved,
      active:   false,
      rejected: isRej,
      notes:    isRej ? asgn.companyNotes : null,
    });
  });

  // CVA + Client review — skip entirely when cancelled
  const anyApproved = !isCancelled && sorted.some(a =>
    ['COMPANY_APPROVED','PENDING_FINAL_APPROVAL','CLIENT_APPROVED','CLIENT_REJECTED','CVA_REJECTED'].includes(a.status)
  );
  const isAccountDoneMulti  = ['PENDING_FINAL_APPROVAL','CLIENT_APPROVED','CLIENT_REJECTED','CVA_REJECTED'].includes(fgStatus);
  const isAccountPendMulti  = fgStatus === 'COMPANY_APPROVED';
  const isCvaDoneMulti      = fgStatus === 'CLIENT_APPROVED';
  const isCvaPendingMulti   = fgStatus === 'PENDING_FINAL_APPROVAL';

  // CANCELLED terminal step — shown instead of client/cva/completed when process was cancelled
  if (isCancelled) {
    steps.push({
      key: 'cancelled', label: 'Cancelled', sublabel: 'Permanently cancelled',
      date: null, done: true, active: false, rejected: true,
    });
  }

  if (anyApproved) {
    steps.push({
      key:      'client',
      label:    isClientRej ? `${clientLabel} Rejected` : `${clientLabel} Review`,
      sublabel: isClientRej ? revisionSublabel(latestVendor, viewerIsVendor) : (isAccountDoneMulti ? 'Approved' : 'Awaiting'),
      date:     null,
      done:     isAccountDoneMulti, active: isAccountPendMulti,
      rejected: isClientRej,
    });
    steps.push({
      key:      'cva',
      label:    isCvaRej ? 'CVA Rejected' : 'CVA Final Approval',
      sublabel: isCvaRej ? revisionSublabel(latestVendor, viewerIsVendor) : (isCvaDoneMulti ? 'Approved & Synced' : (isCvaPendingMulti ? 'Awaiting CVA' : 'Awaiting')),
      date:     isClientDone ? fgClientApprovedAt : null,
      done:     isCvaDoneMulti || isCvaRej, active: isCvaPendingMulti,
      rejected: isCvaRej,
    });

    if (!isClientRej && !isCvaRej) {
      steps.push({
        key: 'completed', label: 'Completed',
        sublabel: isCompleted ? 'Fully approved' : 'Pending',
        date: isCompleted ? fgClientApprovedAt : null,
        done: isCompleted, active: false,
      });
    }
  }

  return steps;
}

function formatAction(action) {
  if (!action) return '—';
  const MAP = {
    COMPANY_APPROVED:     'OmniDrive Approved',      COMPANY_REJECTED:  'OmniDrive Rejected',
    CLIENT_VENDOR_APPROVED: 'CVA Approved',             PENDING_FINAL_APPROVAL: 'Forwarded to Account',
    CLIENT_APPROVED:      'Fully Approved (CVA)',        CLIENT_REJECTED:   'Account Rejected',   CVA_REJECTED: 'CVA Rejected',
    VENDOR_SUBMITTED:     'Submitted for Review',       ASSIGNED:          'Assigned to Vendor',
    WITHDRAWN:            'Assignment Withdrawn',       FINAL_APPROVED:    'Final Approved',
    CANCELLED:                          'Process Permanently Cancelled',
    BOM_UPDATED_BY_ADMIN:               'BOM Edited by OmniDrive',
    BOM_UPDATED_BY_CLIENT:              'BOM Edited by Account',
    BOM_COMPONENT_DELETED_BY_ADMIN:     'BOM Component Deleted by OmniDrive',
    BOM_COMPONENT_DELETED_BY_CLIENT:    'BOM Component Deleted by Account',
    BOM_COMPONENT_DELETED_BY_VENDOR:    'BOM Component Deleted by Vendor',
    DRAFT_SAVED:                        'Draft Saved by Vendor',
    NOTE_ADDED:                         'Note Added',
  };
  return MAP[action] || action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Converts a raw email/username into a readable role label so that
 * personal contact details (email addresses) are never exposed in the history panel.
 * Infers the role from the action type where possible, falls back to domain heuristics.
 */
function roleLabel(action, performedBy, aliases = {}) {
  if (!performedBy) return '—';

  // If we have an explicit alias for this email/username, use it first
  // (e.g. vendor email → vendor company name, hiding PII from clients)
  if (aliases[performedBy]) return aliases[performedBy];

  // Infer directly from the action when unambiguous
  if (['COMPANY_APPROVED', 'COMPANY_REJECTED', 'ASSIGNED', 'CANCELLED',
       'BOM_UPDATED_BY_ADMIN', 'BOM_COMPONENT_DELETED_BY_ADMIN'].includes(action))
    return 'OmniDrive';
  if (['CLIENT_APPROVED', 'CLIENT_REJECTED', 'CVA_REJECTED',
       'BOM_UPDATED_BY_CLIENT', 'BOM_COMPONENT_DELETED_BY_CLIENT'].includes(action))
    return 'Account';
  if (['VENDOR_SUBMITTED', 'DRAFT_SAVED',
       'BOM_COMPONENT_DELETED_BY_VENDOR'].includes(action))
    return 'Vendor';

  // Fall back to domain-based heuristic for ambiguous actions
  const lower = performedBy.toLowerCase();
  if (lower.includes('omnidrive') || lower.includes('admin@')) return 'OmniDrive';
  if (lower.includes('client') || lower.includes('comfort') || lower.includes('living'))
    return 'Account';

  // Default: treat as Vendor (they generate most audit events)
  return 'Vendor';
}
